// =========================================================================================@@
// Last Updated Date: Oct 6, 2023
// Last Updated By: Steven Yuen
// Status Level: 1
// ===========================================================================================

import React, { useEffect, useState } from 'react'
import fetch from 'cross-fetch'
import { Modal, Spacer, Spinner, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import { Link, Route, useHistory, useParams, useRouteMatch } from 'react-router-dom'
import { CloseIcon } from 'assets/icons'
import ResourceSettings from 'src/sites/custom/gns3/Resource/Settings'
import GNS3GithubApplianceBlock from 'src/sites/custom/gns3/Resource/GNS3GithubApplianceBlock'
import PreviewImageBlock from 'src/sites/custom/gns3/Resource/PreviewImageBlock'
import InitiativeDefaultThumbnail from 'src/sites/kits/Initiative/components/DefaultThumbnail'
import ObjectDescriptionBlock from 'src/sites/kits/Object/components/ViewBody/DescriptionBlock'
import ObjectMoreButton from 'src/sites/kits/Object/components/MoreButton'
import ObjectStatusBanner from 'src/sites/kits/Object/components/StatusBanner'
import ObjectTypeLabel from 'src/sites/kits/Object/components/TypeLabel'
import MetadataBlock from 'src/sites/kits/Object/components/ViewAside/MetadataBlock'
import ObjectCommentsBlock from 'src/sites/kits/Object/components/ViewBody/CommentsBlock'
import UserListItemDetailsCard from 'src/sites/kits/People/components/UserListItemDetailsCard'
import { downloadFileAsset } from 'src/sites/kits/Utils'
import { useFileList, useInitiative, useOrganization } from 'src/core/graphql/hooks'
import { InitiativeHierarchyProvider } from 'src/sites/kits/Utils/InitiativeHierarchy'
import { useGlobalState } from 'src/sites/state'
import { Button, EmptyContentBlock, Title } from 'src/sites/kits/UI'

// TODO: This is a stupid GNS3 Hack, but not sure how this should actually work yet.
// See #561
const getGNS3ApplianceData = rawUrl => `/gns3/appliance/data?url=${encodeURIComponent(rawUrl)}`
const getGNS3ApplianceUrl = rawUrl => `/gns3/appliance/download?url=${encodeURIComponent(rawUrl)}`

const ResourceView = ({ displayMode, onCloseButtonClick }) => {
   const isFullscreen = displayMode === 'fullscreen'
   const history = useHistory()
   const { initiativeSlug } = useParams()
   const match = useRouteMatch()
   const [modalIsOpen, setModalIsOpen] = useState(true)
   const [applianceGithubData, setApplianceGithubData] = useState()

   const { organization } = useOrganization()
   const { theme } = useGlobalState()
   const { initiative, networkStatus, refetch } = useInitiative({ slug: initiativeSlug })

   // Resource Primary File
   const { fileList } = useFileList(
      { initiativeId: initiative?.id },
      { skip: !initiative?.id }
   )

   // TODO: This is a stupid GNS3 Hack, but not sure how this should actually work yet.
   // See #561
   const isGNS3Appliance = organization.slug === 'gns3' && initiative?.type.slug === 'appliances'

   useEffect(() => {
      if (isGNS3Appliance && initiative.url) {
         fetch(getGNS3ApplianceData(initiative.url), { method: 'get' }).then((response) => {
            if (response.ok) {
               response.json().then((result) => {
                  setApplianceGithubData(result)
               })
            }
         })
      }
   }, [isGNS3Appliance])

   // We don't want to display our "loading" state if the initiative is being refetched
   const initialLoading = networkStatus <= 2
   if (initialLoading) {
      return (
         <View
            display="flex"
            justifyContent="center"
            alignItems="center"
            padding="60px"
            width="100%"
            minHeight="80vh"
            backgroundColor="#fff"
            borderRadius="5px"
            style={{ overflow: 'hidden' }}>
            <Spinner width="30px" height="30px" color="#aaa" />
         </View>
      )
   }

   const coverUrl = initiative.coverMedia?.file.thumbnailUrlW1600
   const iconUrl = initiative.logoIcon?.file.thumbnailUrlW120
   const initiativeBody = initiative.body.elements[0].body
   const hasPreviewImages = initiative.body.elements?.[1]?.fileInstances.length > 0
   const previewImageFiles = initiative.body.elements?.[1]?.fileInstances.map(i => i.file)
   const hasTags = initiative.tags.length > 0

   return (
      <InitiativeHierarchyProvider
         initiative={initiative}
         refetchInitiative={refetch}>
         <Helmet title={`${initiative.name} | ${organization.name}`} />
         <View
            className="ui-modal"
            marginTop={isFullscreen && theme.viewportMarginTop}
            position="relative"
            width="100%"
            height="100%"
            backgroundColor="#fff"
            borderRadius="5px"
            scroll="on"
            style={{ overflow: 'hidden' }}>
            <ObjectStatusBanner />
            {!isFullscreen && (
               <View
                  position="absolute"
                  top="0px"
                  right="0px"
                  height="70px"
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  padding="0px 24px"
                  zIndex="2">
                  <View onClick={onCloseButtonClick}>
                     <CloseIcon
                        width="24px"
                        height="24px"
                        strokeWidth="1px"
                        color="#888"
                     />
                  </View>
               </View>
            )}
            <View
               paddingLeft={isFullscreen && theme.viewportMarginLeft}
               paddingRight={isFullscreen && theme.viewportMarginRight}>
               <View
                  display="flex"
                  justifyContent="center"
                  width="100%">
                  <View
                     width="100%"
                     maxWidth="900px[a-e] 1200px[f]"
                     paddingHorizontal={isFullscreen && theme.viewportPaddingLeft}>
                     {coverUrl && (
                        <View float="left" width="100%" borderBottom="1px solid #eee">
                           <img
                              src={coverUrl}
                              alt={initiative.name}
                              width="100%"
                           />
                        </View>
                     )}
                     <View float="left" width="100%">
                        <View
                           display="flex"
                           width="100%"
                           paddingTop="64px[a-b] 64px[c-f]"
                           paddingBottom="16px[a-b] 64px[c-f]"
                           paddingHorizontal={!isFullscreen && '16px[a-b] 40px[c-f]'}>
                           <View
                              marginTop="6px"
                              flex="0 0 auto"
                              width="48px[a-b] 90px[c-f]"
                              height="48px[a-b] 90px[c-f]"
                              backgroundColor="#eee"
                              backgroundImage={`url(${iconUrl})`}
                              backgroundSize="cover"
                              border="1px solid var(--primaryLineColor)"
                              borderRadius="9px[a-b] 21px[c-f]"
                              style={{ overflow: 'hidden' }}>
                              {!iconUrl && (
                                 <InitiativeDefaultThumbnail
                                    highlightColor={initiative.type.highlightColor}
                                    iconName={initiative.type.iconName}
                                 />
                              )}
                           </View>
                           <View flex="1 1 auto">
                              <View
                                 display="flex"
                                 justifyContent="space-between">
                                 <View flex="1 1 auto" padding="0px 0px 0px 16px[a-b] 0px 0px 0px 30px[c-f]">
                                    <ObjectTypeLabel />
                                    <Spacer size="1" />
                                    <Title size="xl">
                                       {initiative.name}
                                    </Title>
                                    {initiative.subtitle && (
                                       <>
                                          <Spacer size="2" />
                                          <Text color="#888" size="4">
                                             {initiative.subtitle}
                                          </Text>
                                       </>
                                    )}
                                    <Spacer size="2" />
                                    <div className="ui-meta-text">
                                       {'Posted by '}
                                       <Link to={initiative.addedBy.id} style={{ color: '#888' }}>
                                          {` ${initiative.addedBy.fullName} `}
                                       </Link>
                                       {` • ${initiative.dateAdded}`}
                                    </div>
                                    <View
                                       display="flex"
                                       justifyContent="space-between"
                                       width="100%"
                                       borderTop="1px solid rgba(0,0,0,0.1)"
                                       marginTop="16px[a-b] 30px[c-f]"
                                       paddingTop="16px[a-b] 30px[c-f]">
                                       {initiative.url && initiative.resourceFormat === 'url' && (
                                          <a
                                             href={isGNS3Appliance
                                                ? getGNS3ApplianceUrl(initiative.url)
                                                : initiative.url}
                                             download={isGNS3Appliance}
                                             rel="noopener noreferrer"
                                             target="_blank">
                                             <Button
                                                name="Download"
                                                size="lg"
                                                color={initiative.type.highlightColor || '#000'}
                                             />
                                          </a>
                                       )}
                                       {initiative.resourceFormat === 'files' && (
                                          <Button
                                             onClick={() => downloadFileAsset(fileList.items[0].id)}
                                             name="Download"
                                             size="lg"
                                             color={initiative.type.highlightColor || '#000'}
                                          />
                                       )}
                                       <Spacer size="1" orientation="vertical" />
                                       <ObjectMoreButton />
                                    </View>
                                 </View>
                              </View>
                           </View>
                        </View>
                        <View
                           float="left"
                           width="100%"
                           paddingVertical="24px[a-b] 40px[c-f]"
                           paddingHorizontal={!isFullscreen && '16px[a-b] 40px[c-f]'}
                           borderTop="1px solid var(--primaryLineColor)">
                           <View
                              display="none[a-b] block[c-f]"
                              float="right"
                              width="180px"
                              paddingLeft="25px">
                              <MetadataBlock initiative={initiative} />
                           </View>
                           <View
                              float="left"
                              width="100%[a-b] calc(100% - 180px)[c-f]"
                              minHeight="50vh"
                              paddingRight="15px[c-f]"
                              paddingBottom="50px">
                              {/* eslint-disable max-len */}
                              {applianceGithubData && (
                                 <GNS3GithubApplianceBlock
                                    description={applianceGithubData.description}
                                    documentationUrl={applianceGithubData.documentation_url}
                                    images={applianceGithubData.images}
                                    iou={applianceGithubData.iou}
                                    name={applianceGithubData.name}
                                    qemu={applianceGithubData.qemu}
                                    usage={applianceGithubData.usage}
                                    versions={applianceGithubData.versions}
                                 />
                              )}
                              {/* eslint-enable max-len */}
                              {initiativeBody && (
                                 <ObjectDescriptionBlock truncateText />
                              )}
                              {!initiativeBody && !applianceGithubData?.description && (
                                 <EmptyContentBlock
                                    message={`This ${initiative.type.nameSingular} currently has no description`}
                                 />
                              )}
                              {hasPreviewImages && (
                                 <View
                                    float="left"
                                    width="100%"
                                    padding="20px 0px"
                                    borderTop="1px solid #eee"
                                    borderLeft22="1px solid #e5e5e5"
                                    borderRight22="1px solid #e5e5e5">
                                    <PreviewImageBlock
                                       files={previewImageFiles}
                                    />
                                 </View>
                              )}
                              {hasTags && (
                                 <View
                                    float="left"
                                    width="100%"
                                    paddingVertical="20px[a-d] 20px[e-f]"
                                    borderTop="1px solid #ececec">
                                    <View maxWidth="650px" marginTop="10px">
                                       {initiative.tags.map(tag => (
                                          <View
                                             key={tag}
                                             display="inline-flex"
                                             alignItems="center"
                                             height="25px"
                                             padding="0px 10px"
                                             borderRadius="2px"
                                             marginRight="10px"
                                             marginBottom="10px"
                                             backgroundColor="#eee">
                                             <Text size="2" color="#888">{tag}</Text>
                                          </View>
                                       ))}
                                    </View>
                                 </View>
                              )}
                              <View
                                 float="left"
                                 display="flex"
                                 justifyContent="space-between"
                                 alignItems="center"
                                 borderTop="1px solid #eee"
                                 paddingVertical="16px[a-b] 16px[c-f]"
                                 width="100%">
                                 <View flex="1 1 auto">
                                    <UserListItemDetailsCard
                                       id={initiative.addedBy.id}
                                       avatarMedia={initiative.addedBy.avatarMedia}
                                       borderStyle="none"
                                       fullName={initiative.addedBy.fullName}
                                       organization={initiative.addedBy.organization}
                                       position={initiative.addedBy.position}
                                    />
                                 </View>
                                 <View display="none[a-b]" flex="0 0 auto">
                                    <Button
                                       linkTo={`/-/profile/${initiative.addedBy.id}`}
                                       name="View Profile"
                                       size="sm"
                                       color="#eee"
                                       textColor="#333"
                                       borderRadius="4px"
                                    />
                                 </View>
                              </View>
                              <View
                                 float="left"
                                 width="100%">
                                 <ObjectCommentsBlock />
                              </View>
                           </View>
                        </View>
                     </View>
                  </View>
               </View>
            </View>
         </View>
         {initiative.currentUserCanEdit && (
            <Route path={`${match.path}/-/settings`}>
               <Modal
                  borderRadius="3px"
                  overlayBackgroundColor="rgba(45,40,40,0.9)"
                  width="100%[a-b] 90%[c-d] 100%[e-f]"
                  height="100%"
                  maxWidth="1000px"
                  onCloseComplete={() => {
                     history.push(match.url)
                     setModalIsOpen(true)
                  }}
                  open={modalIsOpen}>
                  <ResourceSettings returnUrl={match.url} />
               </Modal>
            </Route>
         )}
      </InitiativeHierarchyProvider>
   )
}

ResourceView.propTypes = {
   displayMode: PropTypes.oneOf(['fullscreen', 'modal']),
   onCloseButtonClick: PropTypes.func
}

ResourceView.defaultProps = {
   displayMode: 'fullscreen',
   onCloseButtonClick: undefined
}

export default ResourceView
